/**
 * Contract Prices API Client (Q-IMP-3)
 * Zárt szerződéses árjegyzék — Excel import + CRUD
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export interface ContractPriceEntry {
  id: string;
  partnerId: string;
  productId: string;
  customerSku: string;
  customerName: string | null;
  contractPrice: number;
  currency: string;
  validFrom: string;
  validTo: string | null;
  lastPriceUpdate: string | null;
  isOrderable: boolean;
  isExpired: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OrderableProduct {
  productId: string;
  customerSku: string;
  customerName: string | null;
  contractPrice: number;
  currency: string;
  validFrom: string;
  validTo: string | null;
}

export interface ImportRow {
  customerSku: string;
  customerName?: string;
  internalSku: string;
  contractPrice: number;
  isOrderable?: boolean;
}

export interface ImportRowResult {
  row: number;
  customerSku: string;
  internalSku: string;
  status: 'success' | 'error';
  error?: string;
}

export interface ImportResult {
  total: number;
  successful: number;
  failed: number;
  rows: ImportRowResult[];
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get all contract prices for a partner
 */
export async function getContractPrices(
  partnerId: string
): Promise<{ data: ContractPriceEntry[]; meta: { total: number } }> {
  return api.get<{ data: ContractPriceEntry[]; meta: { total: number } }>(
    `/partners/${partnerId}/contract-prices`
  );
}

/**
 * Get orderable products (only valid, active entries)
 */
export async function getOrderableProducts(
  partnerId: string
): Promise<{ data: OrderableProduct[] }> {
  return api.get<{ data: OrderableProduct[] }>(`/partners/${partnerId}/orderable-products`);
}

/**
 * Import contract prices from parsed Excel data (JSON body)
 */
export async function importContractPrices(
  partnerId: string,
  rows: ImportRow[],
  validFrom?: string,
  validTo?: string
): Promise<{ data: ImportResult }> {
  return api.post<{ data: ImportResult }>(`/partners/${partnerId}/contract-prices/import`, {
    rows,
    validFrom,
    validTo,
  });
}

// ============================================
// Q-IMP-3: NEW ENDPOINTS
// ============================================

/** Import diff item for preview (AC-4) */
export interface ImportDiffItem {
  customerSku: string;
  customerName: string | null;
  internalSku: string;
  oldPrice: number | null;
  newPrice: number;
  diffStatus: 'NEW' | 'CHANGED' | 'REMOVED' | 'UNCHANGED';
}

/** Import preview result (AC-4) */
export interface ImportPreviewResult {
  items: ImportDiffItem[];
  added: number;
  changed: number;
  removed: number;
  unchanged: number;
  validationErrors: ImportRowResult[];
}

/**
 * Upload .xlsx file and import contract prices (Q-IMP-3 AC-1)
 */
export async function importContractPricesXlsx(
  partnerId: string,
  file: File,
  force?: boolean
): Promise<{ data: ImportResult }> {
  const formData = new FormData();
  formData.append('file', file);
  const forceParam = force ? '?force=true' : '';
  return api.upload<{ data: ImportResult }>(
    `/partners/${partnerId}/product-references/import/xlsx${forceParam}`,
    formData
  );
}

/**
 * Preview import diff before applying (Q-IMP-3 AC-4)
 */
export async function previewContractPriceImport(
  partnerId: string,
  file: File
): Promise<{ data: ImportPreviewResult }> {
  const formData = new FormData();
  formData.append('file', file);
  return api.upload<{ data: ImportPreviewResult }>(
    `/partners/${partnerId}/product-references/import/preview`,
    formData
  );
}

/**
 * Download .xlsx file with auth headers (Q-IMP-3 AC-5/AC-6).
 * Creates a temporary Blob URL and triggers download.
 */
async function downloadAuthenticatedFile(url: string, filename: string): Promise<void> {
  const { useAuthStore } = await import('@/stores/auth-store');
  const { accessToken, user } = useAuthStore.getState();
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const tenantId = (user as { tenantId?: string } | null)?.tenantId;
  if (tenantId) headers['X-Tenant-ID'] = tenantId;

  const response = await fetch(`/api/v1${url}`, { headers });
  if (!response.ok) throw new Error(`Download failed: HTTP ${response.status}`);

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Download .xlsx template (Q-IMP-3 AC-5)
 */
export async function downloadTemplate(partnerId: string): Promise<void> {
  return downloadAuthenticatedFile(
    `/partners/${partnerId}/contract-prices/template`,
    'arjegyzek-sablon.xlsx'
  );
}

/**
 * Download .xlsx export (Q-IMP-3 AC-6)
 */
export async function downloadExport(partnerId: string): Promise<void> {
  return downloadAuthenticatedFile(
    `/partners/${partnerId}/contract-prices/export`,
    'arjegyzek-export.xlsx'
  );
}
